import { create } from 'zustand';
import { fetchMe, login as apiLogin, logout as apiLogout } from '../api/client';
import type { AuthUser } from '../types/auth';
import { useEditorLayoutStore } from './editorLayoutStore';

// ------------------------------------------------------------
// Auth state (session cookie on server)
// ------------------------------------------------------------

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  checked: boolean;
  error: string | null;

  bootstrap: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  loading: false,
  checked: false,
  error: null,

  bootstrap: async () => {
    set({ loading: true, error: null });
    try {
      const { user } = await fetchMe();
      set({ user, checked: true, loading: false });
      void useEditorLayoutStore.getState().hydrate();
    } catch {
      set({ user: null, checked: true, loading: false });
    }
  },

  login: async (email, password) => {
    set({ loading: true, error: null });
    try {
      const { user } = await apiLogin(email, password);
      set({ user, loading: false, checked: true });
      void useEditorLayoutStore.getState().hydrate();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      set({ error: message, loading: false });
      throw err;
    }
  },

  logout: async () => {
    try {
      await apiLogout();
    } finally {
      set({ user: null, checked: true });
    }
  },

  clearError: () => set({ error: null }),
}));

export function canEdit(permission?: string): boolean {
  return permission === 'edit';
}

export function canRun(permission?: string): boolean {
  return permission === 'edit' || permission === 'run';
}
